import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { CustomCursor, ScrollProgress } from '@/components/motion';
import { MegaNav } from '@/components/ui';
import { SiteFooter } from './site-footer';

/**
 * SiteLayout — the shared shell for the standard content pages (everything other
 * than the bespoke home composition): the fixed MegaNav, the page body via
 * <Outlet/>, and the institutional footer, plus the global scroll-progress bar
 * and custom cursor that the home page also mounts.
 *
 * Because MegaNav is `fixed`, the <main> is padded down so content clears the
 * bar. The route effect handles two cases so every nav link lands correctly:
 *   • a bare path change scrolls back to the top (so navigating between
 *     programmes doesn't strand the viewport mid-page);
 *   • a URL that carries a `#section` hash scrolls that section into view —
 *     retrying on a short timer schedule while the target renders — so the
 *     mega-nav's cross-page deep-links (e.g. /about#eduqua) actually reach the
 *     section rather than the page top. `scroll-mt-*` clears the fixed bar.
 * The scroll is instant (correct for a long cross-page jump and for reduced
 * motion alike).
 */
export function SiteLayout() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }

    // Land a `#section` deep-link on its section. The target can mount a moment
    // after a client-side navigation, and late-loading hero video/images can
    // shift layout afterward, so we re-assert on a short schedule (correcting
    // drift) and bail the moment the visitor scrolls, so we never fight their
    // input. Timers (not rAF) drive the retries so it works even when the tab
    // isn't actively painting. The scroll is instant — a cross-page jump to a
    // section far down the page should not animate thousands of pixels, which is
    // also the correct behaviour under reduced motion. `scroll-mt-*` on each
    // anchor clears the fixed nav bar (scroll-mt-24 ⇒ ~96px).
    const id = decodeURIComponent(hash.slice(1));
    let cancelled = false;
    const timers: ReturnType<typeof setTimeout>[] = [];

    const stop = () => {
      cancelled = true;
    };
    window.addEventListener('wheel', stop, { passive: true });
    window.addEventListener('touchstart', stop, { passive: true });
    window.addEventListener('keydown', stop);
    const cleanup = () => {
      window.removeEventListener('wheel', stop);
      window.removeEventListener('touchstart', stop);
      window.removeEventListener('keydown', stop);
    };

    const attempt = () => {
      if (cancelled) return;
      const el = document.getElementById(id);
      if (el && Math.abs(el.getBoundingClientRect().top - 96) > 4) {
        el.scrollIntoView({ behavior: 'auto', block: 'start' });
      }
    };

    // Immediate try, then a few catch-up passes for late mount / layout shift.
    attempt();
    for (const delay of [40, 120, 260, 500, 900, 1400]) {
      timers.push(setTimeout(attempt, delay));
    }

    return () => {
      cancelled = true;
      for (const t of timers) clearTimeout(t);
      cleanup();
    };
  }, [pathname, hash]);

  return (
    <div className="min-h-dvh bg-background">
      <ScrollProgress />
      <CustomCursor />
      <MegaNav />

      <main className="pt-20 lg:pt-24">
        <Outlet />
      </main>

      <SiteFooter />
    </div>
  );
}
